/**
 * Central business-logic module for the app.
 * This should only tell other modules what to do, not do any work itself
 *
 */

import { getElemWidthHeight } from "../../../utils/js/utils.js";
import { setPageTargeting, setSlotTargeting } from "./targeting.js";
import { detectSPANav } from "./detectSPANav.js";
import { readConfigPath } from "../../../utils/js/configMgr.js";
import {
  getAllSlots,
  getEmptySlots,
  getSlotCfgForContainer,
  isValidFluidSlot,
  resetSlotIndexForRefresh,
} from "./slotHelpers.js";
import { init as initBART } from "./BART.js";
import { init as initCSS } from "./adsCSS.js";
import { init as initGPT } from "./gpt.js";
import { init as initLotame } from "./lotame.js";
import { init as initSponsorshipDetector } from "./sponsorshipDetector.js";
import { init as initTMT } from "./themediatrust.js";
import { init as initWraparound } from "./wraparoundLoader.js";
import { getAdUnit } from "./adUnitHelpers.js";

import { createDependencyPromises } from "../../../../scriptsCore/js/dependencyUtilities.js";
// import configUtilities from "../../../../scriptsCore/js/configUtilities.js";
import events from "@trinitymirrordigital/chameleon-core/src/js/events";
import { logFeature } from "../../../../scriptsCore/js/logging.js";
import debounce from "lodash/debounce";

// to see logs add ?featureDebug=ads to querystring
const log = (...args) => logFeature("ads", ...args);

/**
 * Initializer for the ads module.
 * Loads some dependencies including GPT, and
 * starts the flow for showing ads.
 */
export function initAds() {
  initCSS();
  initGPT();
  initLotame();
  initTMT();
  initBART();
  initWraparound();

  events.listen(window, "gpt.loaded", () => log("gpt.loaded"));
  events.listen(window, "adslotsrendered", () => log("adslotsrendered"));
  events.listen(window, "DOMContentLoaded", () => log("DOMContentLoaded"));
  events.listen(window, "wraparound.loaded", () => log("wraparound.loaded"));
  events.listen(window, "isSponsorship.ready", (e) =>
    log("isSponsorship.ready", e.data)
  );

  // TODO: if BART, add "bart.loaded" to the initPromises
  const initPromises = createDependencyPromises("adsInitDependencies");
  initPromises
    .then(() => {
      window.googletag.cmd.push(setup);
    })
    .catch(() => {
      window.googletag = window.googletag || { cmd: [] };
      window.googletag.cmd.push(setup);
    });
}

/**
 * A continuation of the initializer, now that we know GPT is on the page.
 */
async function setup() {
  log("setup");
  initSponsorshipDetector();

  configureGPT();
  setPageTargeting();

  const numSlotsExpected = readConfigPath(
    "publicSectionParams.countSlotsToWaitBeforeInitialAdRequest"
  );
  await findSlotsAndLoadAds(numSlotsExpected);

  scanForLayoutUpdates();
}

async function findSlotsAndLoadAds(numSlotsExpected = 0) {
  // why empty ones now?
  // - the 'empty' func waits until the specified num of slots appear on page.
  // IDK why empty seemed like a good idea.
  const slotDivs = numSlotsExpected
    ? await getEmptySlots(numSlotsExpected)
    : getAllSlots();
  log("got slotDivs");
  const slotData = await prepareSlots(slotDivs);
  log("got slotData");
  return await loadAds(slotData);
}

/**
 * Calls some methods of GPT pubads() to configure it
 */
function configureGPT() {
  log("configureGPT");
  const pubads = window.googletag.pubads;
  pubads().enableSingleRequest();
  pubads().enableAsyncRendering();
  // pubads().collapseEmptyDivs();
  pubads().setCentering(true);
  // pubads().enableVideoAds();
}

/**
 * Accepts an array of slot-container divs (or other HTMLElement), and performs a few tasks
 * to get them ready for GPT to load ads into them
 * @param {array} slotDivs array of HTMLElements
 * @returns {array} an array of objects containing data about each slot, for GPT
 */
function prepareSlots(slotDivs) {
  const slotData = slotDivs
    .map(getSlotCfgForContainer)
    .filter((slotMeta) => slotMeta.slotId) // this means we didn't find a matching slotCfg for the container
    .map(addPageData)
    .map(prepareSlot);
  events.fire(document.body, "gpt.slots.registered");
  return slotData;
}

/**
 * Prepare a single slot-container div for ad injection.
 * - Figures out which slot-id to use
 * - injects the div with the slot-id
 * - calls pubads().defineslot()
 * - set slot targeting
 * @param {object} slotMeta data about the slot container elem
 * @returns {object} a copy of the slotMEta object, with the GPT slot added
 */
function prepareSlot(slotMeta) {
  const { container, slotId, adUnit, sizes } = slotMeta;
  if (container && slotId && adUnit && sizes) {
    // remove larger sizes  from targeting to prevent layout jank
    const [width, height] = getElemWidthHeight(container);
    const validSizes = sizes.filter(([w, h]) => {
      return (
        (w - width < 10 && h - height < 10) || isValidFluidSlot(container, w)
      );
    });

    injectSlotDiv(container, slotId);

    // do googstuff
    const slot = window.googletag.defineSlot(adUnit, validSizes, slotId);
    if (slot && slot.addService) {
      try {
        slot.addService(window.googletag.pubads());
        setSlotTargeting(slot, slotMeta);
        return { ...slotMeta, slot };
      } catch (e) {
        console.warn("ERROR:", e);
        return null;
      }
    }
  }
}

/**
 * Adds page-specific data to the data for a specific slot
 * - AdUnit name
 * @param {object} slotMeta
 * @returns{object} a copy of the slotMeta object with the adUnit string added
 */
function addPageData(slotMeta) {
  const adUnit = getAdUnit();
  return Object.assign({}, slotMeta, { adUnit });
}

/**
 * Create and appendChild a new div with a specified ID into a container element
 * The new div will ***replace*** any existing contents of the container.
 * @param {HTMLELement} container
 * @param {string} slotId
 */
function injectSlotDiv(container, slotId) {
  const slotDiv = document.createElement("div");
  slotDiv.id = slotId;
  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }
  log("append slot div", slotId);
  container.appendChild(slotDiv);
}

/**
 * Tell GPT to load some ads into some specified slots
 * @param {array} slotData array of 'slotMEta' objects containing data about all the slots we want to load at this time
 */
async function loadAds(slotData) {
  log("loadAds");
  window.googletag.enableServices();

  const showAds = () => {
    log("showAds");
    slotData.forEach((slotMeta) => {
      window.googletag.display(slotMeta.slotId);
    });
    events.fire(document.body, "gpt.ads.requested");
  };
  // TODO: add header bidding and CMP to adPromises
  const adPromises = createDependencyPromises("adDependencies");
  //   .then(() => {
  //     log("adDependencies");
  showAds();
  //   })
  //   .catch((e) => {
  //     log("catch adDependencies", e);
  //     showAds();
  //   });
  return adPromises;
}

/**
 * Detect changes in layout that might need to be handled by reloading ads.
 * Subscribes to an event defined in the site-specific config.
 */
async function scanForLayoutUpdates() {
  log("scanForLazySlots");
  const eventName = readConfigPath("publicSectionParams.checkNewSlotsEvent");

  const handler = debounce(handleLayoutUpdates, 300);
  if (eventName) {
    events.listen(window, eventName, handler);
  }
}

/**
 * Handler for any event that might cause us to need to load or reload some ads
 */
async function handleLayoutUpdates() {
  log("handleLayoutUpdates");
  // figure out what's changed and react accordingly

  if (detectSPANav()) {
    log("SPAnav");
    refreshSlots();
    return;
  }

  // const resizedSlots = getResizedSlots();
  // // probably the window was resized causing a change in breakpoint
  // if (resizedSlots.length) {
  //   log(`...got ${resizedSlots.length} RESIZED slots to reload`, resizedSlots);
  //   lazyLoadAds(resizedSlots);
  //   return;
  // }

  const emptySlotDivs = await getEmptySlots();
  if (emptySlotDivs.length) {
    log(`...got ${emptySlotDivs.length} empty slots to lazyload`);
    // some more slots to lazy-load
    const isTakeover = await detectTakeover();
    log("isTakeover", isTakeover);
    if (isTakeover) {
      collapseSlots(emptySlotDivs);
    } else {
      lazyLoadAds(emptySlotDivs);
    }
    return;
  }
}

/**
 * Reload all ad slots, keeping in mind that if it's an SPA,
 * the adUnit and page targeting might have changed, so we update
 * those too
 */
function refreshSlots() {
  window.googletag.destroySlots();
  events.fire(window, "gpt.slots.destroyed");
  resetSlotIndexForRefresh();
  setPageTargeting();
  findSlotsAndLoadAds();
}

/**
 * Load some more ads into containers that were not present when we first loaded ads.
 * @param {array} slotDivs list of container HTMLElements to inject ads into
 */
async function lazyLoadAds(slotDivs) {
  log("lazyLoadAds", slotDivs);
  const slotData = await prepareSlots(slotDivs);
  await loadAds(slotData);
}

function collapseSlots(slotDivs) {
  log("collapseSlots", slotDivs);
  slotDivs.forEach((elem) => {
    elem.classList.add("collapse-slot");
    const inlineStyle = elem.getAttribute("style");
    if (inlineStyle) {
      if (inlineStyle.includes("display:")) {
        elem.style.display = "none";
      }
    }
  });
}

async function detectTakeover() {
  return new Promise((resolve) => {
    events.listen(window, "isSponsorship.ready", (e) => {
      resolve(e.data.isSponsorship);
    });
  });
}
